import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	useDeleteAvatarsFetilizer,
	useDeleteDefensive,
	useDeleteFertilizer,
	useDeleteManufacturer,
	useGetManufacturers,
} from '@practium/features/api';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useDeleteAvatarsDefensive } from '../../api/hooks/defensives/useDeleteAvatarsDefensive';
import type { DefensiveFormRef } from '../components/Forms/DefensiveForm/DefensiveForm';
import { DefensiveForm } from '../components/Forms/DefensiveForm/DefensiveForm';
import type { FertilizerFormRef } from '../components/Forms/FertilizerForm/FertilizerForm';
import { FertilizerForm } from '../components/Forms/FertilizerForm/FertilizerForm';
import type { ManufacturerFormRef } from '../components/Forms/ManufacturerForm/ManufacturerForm';
import { ManufacturerForm } from '../components/Forms/ManufacturerForm/ManufacturerForm';
import { ProductImagesCard } from '../components/ProductImagesCard/ProductImagesCard';
import type {
	DefensiveFormData,
	FertilizerFormData,
	Manufacturer,
	ManufacturerFormData,
	ProductPageMode,
	ProductsTab,
} from '../types';
import styles from './AddProductPage.module.css';

const PRODUCT_TITLES: Record<ProductsTab, Record<ProductPageMode, string>> = {
	defensives: {
		add: 'Novo Defensivo Agrícola',
		edit: 'Editar Defensivo Agrícola',
		view: 'Informações do Defensivo',
	},
	fertilizers: {
		add: 'Novo Fertilizante Agrícola',
		edit: 'Editar Fertilizante Agrícola',
		view: 'Informações do Fertilizante',
	},
	manufacturers: {
		add: 'Novo Fabricante',
		edit: 'Editar Fabricante',
		view: 'Informações do Fabricante',
	},
};

const SUBTITLES: Record<ProductPageMode, string> = {
	add: 'Cadastre os dados técnicos do novo produto',
	edit: 'Edite os dados técnicos do produto',
	view: 'Visualize os dados técnicos do produto',
};

export interface AddProductPageProps {
	type: ProductsTab;
	onBack: () => void;
	initialMode?: ProductPageMode;
	initialData?: DefensiveFormData | FertilizerFormData | ManufacturerFormData;
	onDelete?: () => void;
	onDeleteImage?: (tyep: string, id: string) => void;
	canEdit?: boolean;
	preview3DSlot?: ReactNode;
	onIdentifierChange?: (name: string, nature: string, manufacturer: string, extra: string) => void;
	id?: string;
}

export const AddProductPage = ({
	type,
	onBack,
	initialMode = 'add',
	initialData,
	onDelete,
	onDeleteImage,
	canEdit = true,
	preview3DSlot,
	onIdentifierChange,
	id,
}: AddProductPageProps) => {
	const initialImages =
		initialData && 'avatars' in initialData && Array.isArray(initialData.avatars)
			? initialData.avatars.map((avatar) => avatar.url)
			: [];
	const [files, setFiles] = useState<File[]>([]);
	const [imageUrls, setImageUrls] = useState<string[]>(initialImages);
	const [currentMode, setCurrentMode] = useState<ProductPageMode>(initialMode);
	const [isLoading, setIsLoading] = useState(false);
	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess, error: toastError } = useToast();

	const defensiveRef = useRef<DefensiveFormRef>(null);
	const fertilizerRef = useRef<FertilizerFormRef>(null);
	const manufacturerRef = useRef<ManufacturerFormRef>(null);

	const { data: manufacturersData } = useGetManufacturers();
	const { mutateAsync: deleteAvatarDefensive } = useDeleteAvatarsDefensive();
	const { mutateAsync: deleteAvatarFertilizer } = useDeleteAvatarsFetilizer();

	const manufacturers: Manufacturer[] = manufacturersData ?? [];

	const { mutateAsync: deleteDefensive } = useDeleteDefensive();
	const { mutateAsync: deleteFertilizer } = useDeleteFertilizer();
	const { mutateAsync: deleteManufacturer } = useDeleteManufacturer();

	const getTitle = () => {
		if (currentMode === 'view' && initialData) {
			if (type === 'defensives') return PRODUCT_TITLES.defensives.view;
			if (type === 'fertilizers') return PRODUCT_TITLES.fertilizers.view;
			if (type === 'manufacturers') return PRODUCT_TITLES.manufacturers.view;
		}
		return PRODUCT_TITLES[type][currentMode];
	};

	const handleSubmit = useCallback(() => {
		onBack();
	}, [onBack]);

	const isFormDirty = useCallback(() => {
		if (files.length > 0) return true;
		if (type === 'defensives') return defensiveRef.current?.isDirty?.() ?? false;
		if (type === 'fertilizers') return fertilizerRef.current?.isDirty?.() ?? false;
		return manufacturerRef.current?.isDirty?.() ?? false;
	}, [files, type]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			if (currentMode === 'edit' && initialMode === 'view') {
				setCurrentMode('view');
			} else {
				onBack();
			}
		};
		if (!isFormDirty()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [confirm, currentMode, initialMode, isFormDirty, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir produto',
			message: 'Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});

		if (confirmed && id) {
			setIsLoading(true);
			try {
				if (type === 'defensives') {
					await deleteDefensive(id);
				} else if (type === 'fertilizers') {
					await deleteFertilizer(id);
				} else if (type === 'manufacturers') {
					await deleteManufacturer(id);
				}
				toastSuccess('Registro excluído com sucesso.');
				onDelete?.();
			} catch (error) {
				toastError(error instanceof Error ? error.message : 'Erro ao excluir o registro.');
			} finally {
				setIsLoading(false);
			}
		}
	}, [
		confirm,
		id,
		type,
		deleteDefensive,
		deleteFertilizer,
		deleteManufacturer,
		toastSuccess,
		toastError,
		onDelete,
	]);

	const handleRemoveImage = useCallback(
		async (url: string) => {
			const confirmed = await confirm({
				title: 'Excluir imagem',
				message: 'Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.',
				confirmText: 'Excluir',
				cancelText: 'Cancelar',
				variant: 'danger',
			});

			if (!confirmed || !url || !id || !type) return;

			setIsLoading(true);

			try {
				if (type === 'defensives') {
					await deleteAvatarDefensive({ id, url });
				} else if (type === 'fertilizers') {
					await deleteAvatarFertilizer({ id, url });
				}

				toastSuccess('Imagem apagada!');
				onDeleteImage?.(id, type);
			} catch (error) {
				toastError(error instanceof Error ? error.message : 'Erro ao excluir a imagem.');
			} finally {
				setIsLoading(false);
			}
		},
		[
			confirm,
			type,
			id,
			deleteAvatarDefensive,
			deleteAvatarFertilizer,
			toastSuccess,
			toastError,
			onDeleteImage,
		]
	);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		if (type === 'defensives') defensiveRef.current?.submit();
		else if (type === 'fertilizers') fertilizerRef.current?.submit();
		else manufacturerRef.current?.submit();
	}, [type]);

	const sharedFormProps = {
		mode: currentMode,
		onSubmit: handleSubmit,
		onLoadingChange: setIsLoading,
		onIdentifierChange,
		id,
		file: files,
	};

	const pageActions = (
		<div className={styles.pageActions}>
			<FormActions
				mode={currentMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isLoading}
				onDelete={onDelete ? handleDelete : undefined}
				onEdit={canEdit ? handleEdit : undefined}
			/>
		</div>
	);

	const confirmModal = (
		<ConfirmModal
			isOpen={isConfirmOpen}
			title={confirmOptions.title}
			message={confirmOptions.message}
			confirmText={confirmOptions.confirmText}
			cancelText={confirmOptions.cancelText}
			variant={confirmOptions.variant}
			onConfirm={handleConfirm}
			onCancel={handleConfirmCancel}
		/>
	);

	if (type === 'manufacturers') {
		return (
			<div className={styles.manufacturerPage}>
				<div className={styles.manufacturerContent}>
					<PageHeader title={getTitle()} subtitle={SUBTITLES[currentMode]} onBack={onBack} />
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Técnicas"
						className={styles.fullCard}
					>
						<ManufacturerForm
							ref={manufacturerRef}
							{...sharedFormProps}
							initialData={initialData as ManufacturerFormData | undefined}
							id={id}
						/>
					</FormCard>
					{pageActions}
					{confirmModal}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.page}>
			<PageHeader title={getTitle()} subtitle={SUBTITLES[currentMode]} onBack={onBack} />
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Técnicas"
						className={styles.mainCard}
					>
						{type === 'fertilizers' && (
							<FertilizerForm
								ref={fertilizerRef}
								manufacturers={manufacturers}
								{...sharedFormProps}
								initialData={initialData as FertilizerFormData | undefined}
								id={id}
								file={files}
							/>
						)}
						{type === 'defensives' && (
							<DefensiveForm
								ref={defensiveRef}
								manufacturers={manufacturers}
								{...sharedFormProps}
								initialData={initialData as DefensiveFormData | undefined}
								id={id}
								file={files}
							/>
						)}
					</FormCard>
					{pageActions}
				</div>
				<ProductImagesCard
					mode={currentMode}
					preview3DSlot={preview3DSlot}
					className={styles.sideCard}
					files={files}
					onFilesChange={setFiles}
					imageUrls={imageUrls}
					onImageUrlsChange={setImageUrls}
					onRemoveImage={handleRemoveImage}
				/>
			</div>

			{confirmModal}
		</div>
	);
};
